/**
 * DuelAnalytics — Full duel stats with Pro gating.
 * Free: W-L-D, win rate. Pro: streak, best streak, avg duration, favorite type.
 */

import { useEffect, useState } from 'react';
import { View, Text, StyleSheet, ActivityIndicator, TouchableOpacity } from 'react-native';
import { useTheme } from '@/contexts/ThemeContext';
import { useAuth } from '@/contexts/AuthContext';
import { useSubscription } from '@/hooks/useSubscription';
import { supabase } from '@/lib/supabase';
import { createLogger } from '@/lib/logger';
import { useRouter } from 'expo-router';
import { Lock } from 'lucide-react-native';

const log = createLogger('DUEL_ANALYTICS');

interface AnalyticsData {
  wins: number;
  losses: number;
  draws: number;
  winRate: number;
  currentStreak: number;
  bestStreak: number;
  avgDurationHours: number;
  favoriteDuelType: string | null;
}

/** Pure-RN "donut" built from two overlapping half-circle masks. */
function WinRateDonut({
  winRate,
  size,
  strokeWidth,
  winColor,
  lossColor,
  bgColor,
}: {
  winRate: number;
  size: number;
  strokeWidth: number;
  winColor: string;
  lossColor: string;
  bgColor: string;
}) {
  const radius = size / 2;
  const inner = size - strokeWidth * 2;
  const pct = Math.max(0, Math.min(1, winRate / 100));
  const deg = pct * 360;

  return (
    <View style={{ width: size, height: size, alignItems: 'center', justifyContent: 'center' }}>
      <View
        style={{
          position: 'absolute',
          width: size,
          height: size,
          borderRadius: radius,
          backgroundColor: lossColor,
        }}
      />
      {pct > 0 && (
        <View
          style={{
            position: 'absolute',
            width: size / 2,
            height: size,
            left: size / 2,
            overflow: 'hidden',
          }}>
          <View
            style={{
              width: size,
              height: size,
              borderRadius: radius,
              backgroundColor: winColor,
              marginLeft: -size / 2,
              transform: [{ rotate: `${Math.min(deg, 180)}deg` }],
            }}
          />
        </View>
      )}
      {pct > 0.5 && (
        <View
          style={{
            position: 'absolute',
            width: size / 2,
            height: size,
            left: 0,
            overflow: 'hidden',
          }}>
          <View
            style={{
              width: size,
              height: size,
              borderRadius: radius,
              backgroundColor: winColor,
              transform: [{ rotate: `${deg - 180}deg` }],
            }}
          />
        </View>
      )}
      <View
        style={{
          width: inner,
          height: inner,
          borderRadius: inner / 2,
          backgroundColor: bgColor,
          alignItems: 'center',
          justifyContent: 'center',
        }}>
        <Text style={[styles.donutPct, { color: winColor }]}>
          {Math.round(winRate)}%
        </Text>
        <Text style={[styles.donutLabel, { color: lossColor }]}>Win Rate</Text>
      </View>
    </View>
  );
}

function ProLockedStat({ label, c }: { label: string; c: any }) {
  const router = useRouter();
  return (
    <View style={styles.statItem}>
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 3 }}>
        <Lock size={12} color={c.textTertiary} />
        <Text style={[styles.statValue, { color: c.textTertiary }]}>—</Text>
      </View>
      <Text style={[styles.statLabel, { color: c.textTertiary }]}>{label}</Text>
    </View>
  );
}

export default function DuelAnalytics() {
  const { theme } = useTheme();
  const { profile } = useAuth();
  const { isProPlus } = useSubscription();
  const router = useRouter();
  const c = theme.colors;
  const [data, setData] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!profile?.id) return;
    loadAnalytics(profile.id);
  }, [profile?.id]);

  const loadAnalytics = async (userId: string) => {
    setLoading(true);
    try {
      const { data: duels, error } = await supabase
        .from('duels')
        .select('id, status, winner_user_id, challenger_id, challenged_id, duel_type, timeframe_hours, created_at, end_at')
        .or(`challenger_id.eq.${userId},challenged_id.eq.${userId}`)
        .in('status', ['completed', 'expired', 'cancelled', 'declined'])
        .order('created_at', { ascending: true });

      if (error) throw error;
      if (!duels || duels.length === 0) {
        setData(null);
        setLoading(false);
        return;
      }

      let wins = 0;
      let losses = 0;
      let draws = 0;
      let bestStreak = 0;
      let currentStreak = 0;
      let totalDurationHours = 0;
      let durationCount = 0;
      const typeCounts: Record<string, number> = {};

      for (const d of duels) {
        if (d.status === 'cancelled') {
          draws++;
          currentStreak = 0;
          continue;
        }

        if (d.status === 'expired') {
          draws++;
          currentStreak = 0;
          continue;
        }

        // completed / resolved
        if (d.winner_user_id === userId) {
          wins++;
          currentStreak++;
          bestStreak = Math.max(bestStreak, currentStreak);
        } else if (d.winner_user_id) {
          losses++;
          currentStreak = 0;
        } else {
          draws++;
          currentStreak = 0;
        }

        // Duration tracking
        if (d.created_at && d.end_at) {
          const hrs = (new Date(d.end_at).getTime() - new Date(d.created_at).getTime()) / 3600000;
          if (hrs > 0 && hrs < 10000) {
            totalDurationHours += hrs;
            durationCount++;
          }
        }

        // Type tracking
        if (d.duel_type) {
          typeCounts[d.duel_type] = (typeCounts[d.duel_type] || 0) + 1;
        }
      }

      const total = wins + losses + draws;
      const favType = Object.entries(typeCounts).sort((a, b) => b[1] - a[1])[0]?.[0] ?? null;

      setData({
        wins,
        losses,
        draws,
        winRate: total > 0 ? (wins / total) * 100 : 0,
        currentStreak,
        bestStreak,
        avgDurationHours: durationCount > 0 ? totalDurationHours / durationCount : 0,
        favoriteDuelType: favType,
      });
    } catch (err) {
      log.error('Failed to load duel analytics:', err);
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <View style={[styles.container, { backgroundColor: c.surface, borderColor: c.border }]}>
        <ActivityIndicator color={c.accent} />
      </View>
    );
  }

  if (!data || (data.wins + data.losses + data.draws) === 0) return null;

  const total = data.wins + data.losses + data.draws;

  return (
    <View style={[styles.container, { backgroundColor: c.surface, borderColor: c.border }]}>
      <View style={styles.topRow}>
        <WinRateDonut
          winRate={data.winRate}
          size={90}
          strokeWidth={12}
          winColor={c.success}
          lossColor={c.error + '44'}
          bgColor={c.surface}
        />

        <View style={styles.statsGrid}>
          <View style={styles.statItem}>
            <Text style={[styles.statValue, { color: c.text }]}>
              {data.wins}-{data.losses}-{data.draws}
            </Text>
            <Text style={[styles.statLabel, { color: c.textSecondary }]}>W-L-D</Text>
          </View>
          <View style={styles.statItem}>
            <Text style={[styles.statValue, { color: c.text }]}>{total}</Text>
            <Text style={[styles.statLabel, { color: c.textSecondary }]}>Total</Text>
          </View>

          {/* Pro-gated stats */}
          {isProPlus ? (
            <>
              <View style={styles.statItem}>
                <Text style={[styles.statValue, { color: data.currentStreak > 0 ? c.success : c.text }]}>
                  {data.currentStreak}🔥
                </Text>
                <Text style={[styles.statLabel, { color: c.textSecondary }]}>Streak</Text>
              </View>
              <View style={styles.statItem}>
                <Text style={[styles.statValue, { color: c.accent }]}>{data.bestStreak}</Text>
                <Text style={[styles.statLabel, { color: c.textSecondary }]}>Best Streak</Text>
              </View>
            </>
          ) : (
            <>
              <ProLockedStat label="Streak" c={c} />
              <ProLockedStat label="Best Streak" c={c} />
            </>
          )}
        </View>
      </View>

      {/* Bottom row: Pro-only advanced stats */}
      <View style={styles.bottomRow}>
        {isProPlus ? (
          <>
            {data.avgDurationHours > 0 && (
              <View style={[styles.infoPill, { backgroundColor: c.surfaceVariant }]}>
                <Text style={[styles.infoPillLabel, { color: c.textSecondary }]}>AVG DURATION</Text>
                <Text style={[styles.infoPillValue, { color: c.text }]}>
                  {data.avgDurationHours < 1
                    ? `${Math.round(data.avgDurationHours * 60)}m`
                    : `${Math.round(data.avgDurationHours)}h`}
                </Text>
              </View>
            )}
            {data.favoriteDuelType && (
              <View style={[styles.infoPill, { backgroundColor: c.surfaceVariant }]}>
                <Text style={[styles.infoPillLabel, { color: c.textSecondary }]}>FAVORITE TYPE</Text>
                <Text style={[styles.infoPillValue, { color: c.text }]} numberOfLines={1}>
                  {data.favoriteDuelType}
                </Text>
              </View>
            )}
          </>
        ) : (
          <TouchableOpacity
            style={[styles.upgradePill, { backgroundColor: c.accent + '12', borderColor: c.accent + '30' }]}
            onPress={() => router.push('/paywall' as any)}
            activeOpacity={0.7}
          >
            <Lock size={14} color={c.accent} />
            <Text style={[styles.upgradeText, { color: c.accent }]}>
              Upgrade to Pro for full stats
            </Text>
          </TouchableOpacity>
        )}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    borderWidth: 1,
    borderRadius: 2,
    padding: 16,
    gap: 14,
  },
  topRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
  },
  donutPct: {
    fontSize: 20,
    fontWeight: '900',
  },
  donutLabel: {
    fontSize: 11,
    fontWeight: '600',
    marginTop: -2,
  },
  statsGrid: {
    flex: 1,
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  statItem: {
    width: '45%' as any,
    alignItems: 'center',
  },
  statValue: {
    fontSize: 18,
    fontWeight: '900',
  },
  statLabel: {
    fontSize: 11,
    fontWeight: '600',
  },
  bottomRow: {
    flexDirection: 'row',
    gap: 10,
  },
  infoPill: {
    flex: 1,
    borderRadius: 2,
    padding: 10,
    gap: 2,
  },
  infoPillLabel: {
    fontSize: 11,
    fontWeight: '700',
  },
  infoPillValue: {
    fontSize: 14,
    fontWeight: '800',
  },
  upgradePill: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    paddingVertical: 10,
    paddingHorizontal: 14,
    borderRadius: 0,
    borderWidth: 1,
  },
  upgradeText: {
    fontSize: 13,
    fontWeight: '700',
  },
});
